/**
 * #987 — the #988 manual ticket on the real US clients.
 *
 * Each mounted ticket owns one trading client and one market client, so its
 * draft, preview and single submit can never be shared with another surface.
 * The trading client's validated preview and submit result are mapped to the
 * presenter's shapes by `ticketPresentation.ts`; nothing here retries,
 * resubmits or acknowledges a request. Used by the standalone US page and by
 * Game Detail (with a host-bound target).
 *
 * `editOrder` (#1017) opens the ticket as an edit of that resting order. Each
 * ticket owns a portfolio client for that cancel, separate from any portfolio
 * panel's (which runs one cancel at a time).
 */
import { useMemo, useState } from 'react';
import { createInstitutionalDraftInspector } from '@/lib/polymarketUs/institutionalDraftClient';
import type { UsAccountContext } from '@/lib/polymarketUs/accountClient';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import { createUsBrowserTradingClient } from '@/lib/polymarketUs/browserTradingClient';
import { mapUsTicketPreview, mapUsTicketResult } from '@/lib/polymarketUs/ticketPresentation';
import type { UsMarketClient } from '@/lib/polymarketUs/marketClient';
import type { UsPortfolioClient } from '@/lib/polymarketUs/portfolioClient';
import type { UsCloseEvent, UsOrderRow } from '@/lib/polymarketUs/types';
import type { UsTradingClient } from '@/lib/polymarketUs/tradingClient';
import type { UsDraftTerms } from '@/lib/polymarketUs/ticketModel';
import { useDisplaySettings } from '@/stores/displaySettings';
import type { UsTicketTrading } from './ticketAdapter';
import { UsManualTicket, type UsLockedTarget, type UsHostMarketReadiness, type UsRailControls } from './UsManualTicket';

export type { UsHostMarketReadiness, UsRailControls } from './UsManualTicket';

export interface ConnectedUsTicketProps {
  railControls?: UsRailControls;
  context: UsAccountContext;
  onRequestRecorded(): void;
  lockedTarget?: UsLockedTarget;
  priceHintCents?: number | null;
  /** Which pick `priceHintCents` is (see UsManualTicket). */
  priceHintSeq?: string | null;
  hostMarketReadiness?: UsHostMarketReadiness;
  /** The host's memory of the trader's terms, and where edits report (see UsManualTicket). */
  draftTerms?: UsDraftTerms | null;
  onDraftTermsChange?(terms: UsDraftTerms): void;
  layout?: 'page' | 'rail';
  /** Replace this resting order: cancel, confirm, then place. */
  editOrder?: UsOrderRow;
  /** #1006: close this holding of the locked target (see UsManualTicket). */
  closeHolding?: { quantity: string };
  /** #1006: where this close's one order stands (see UsManualTicket). */
  onCloseSent?(event: UsCloseEvent): void;
  /** #1006: an edit's replacement was sent under this intent (see UsManualTicket). */
  onEditReplaced?(intentId: string): void;
  /** #1006: a cancel or submit is in flight; don't dismiss the host window. */
  onCommittedChange?(committed: boolean): void;
  /** Tests inject clients and a clock; the app always builds its own clients.
   * `status` (#1247) reads the last submission back; injected clients without
   * it leave the receipt as sent. */
  clients?: { trading: UsTradingClient; markets: UsMarketClient; portfolio?: UsPortfolioClient; status?: UsPortfolioClient };
  clock?: () => number;
}

/** Adapts one trading client to the presenter for one account and scope. */
export function usTicketTrading(client: UsTradingClient, context: UsAccountContext): UsTicketTrading {
  const { accountId, scope } = context;
  return {
    setScope: (next) => client.setScope(next),
    setDraft: (draft) => client.setDraft(draft),
    preview: async () => mapUsTicketPreview(await client.preview()),
    submit: async () => {
      const result = await client.submit();
      const mapped = mapUsTicketResult(result, accountId, scope);
      return result.kind === 'receipt' && result.limit ? { ...mapped, limit: result.limit } : mapped;
    },
    acknowledge: (requestKey) => client.acknowledge(requestKey),
    canSubmit: () => client.canSubmit(),
  };
}

export function ConnectedUsTicket({ context, clients, editOrder, ...rest }: ConnectedUsTicketProps) {
  const [own] = useState(() => clients ?? { trading: createUsBrowserTradingClient(), markets: createUsBrowserMarketClient() });
  // Always built, so an edit handed in after mount still works; construction sends nothing.
  const [inspector] = useState(() => createInstitutionalDraftInspector());
  const [portfolio] = useState(() => clients?.portfolio ?? createUsBrowserPortfolioClient());
  // #1247: a second portfolio client, only for reading the last submission
  // back: its reads never race the edit's cancel on the client above.
  const [status] = useState(() => clients ? clients.status : createUsBrowserPortfolioClient());
  const { accountId, scope } = context;
  // Stable per account and scope: the presenter rebinds only when these change.
  const trading = useMemo(
    () => usTicketTrading(own.trading, context),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebinding follows identity, not snapshot object churn
    [own.trading, accountId, scope.scopeKey, scope.revision],
  );
  const defaultContracts = useDisplaySettings((s) => s.defaultContracts);
  const quickSizes = useDisplaySettings((s) => s.quickSizes);
  const sizePresets = useMemo(() => ({ defaultContracts, quickSizes }), [defaultContracts, quickSizes]);
  const defaultExpiryMode = useDisplaySettings((s) => s.defaultPolyUsExpiryMode);
  return (
    <UsManualTicket context={context} trading={trading} markets={own.markets} sizePresets={sizePresets} defaultExpiryMode={defaultExpiryMode}
      inspectInstitutionalDraft={context.protocol === 'institutional' ? inspector : undefined}
      editOrder={editOrder} cancel={portfolio} orderStatus={status} {...rest} />
  );
}
